// Motion utilitário compartilhado entre dialog/sheet/dropdown/popover/etc.
//
// Centraliza dois comportamentos repetidos:
//   1. Detecção de `prefers-reduced-motion: reduce` — quando ativo, qualquer
//      espera por animação é pulada (o usuário não deve esperar transições).
//   2. Aplicar uma classe de animação e resolver uma Promise quando ela termina
//      (`animationend`), com timeout de segurança caso o evento não dispare
//      (ex.: animação removida via CSS, elemento escondido no meio).
//
// Uso típico ao FECHAR um overlay com animação de saída:
//
//   import { animateOut } from "./utils/motion"
//
//   async close() {
//     await animateOut(this.panelTarget, "animate-overlay-out")
//     this.panelTarget.classList.add("hidden")
//   }
//
// Ao ABRIR, normalmente basta aplicar a classe de entrada e deixar o CSS rodar;
// `applyEnter` existe pra garantir que a classe seja re-disparada mesmo que já
// estivesse presente (reflow forçado).

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"

export function prefersReducedMotion() {
  return window.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false
}

// Aplica uma classe de animação de ENTRADA, reiniciando-a se já presente.
export function applyEnter(el, enterClass) {
  if (!el) return
  el.classList.remove(enterClass)
  // Reflow força o browser a reconhecer a remoção antes de re-adicionar,
  // garantindo que a animação rode de novo (re-open sem desmontar o nó).
  void el.offsetWidth
  el.classList.add(enterClass)
}

// Aplica uma classe de animação de SAÍDA e resolve quando ela termina.
// Pula a espera (resolve imediato) quando reduced-motion está ativo.
export function animateOut(el, exitClass, { fallbackMs = 400 } = {}) {
  if (!el || prefersReducedMotion()) return Promise.resolve()

  return new Promise((resolve) => {
    let done = false
    const finish = () => {
      if (done) return
      done = true
      el.removeEventListener("animationend", onEnd)
      el.classList.remove(exitClass)
      resolve()
    }
    const onEnd = (e) => {
      // Ignora animationend borbulhado de filhos.
      if (e.target === el) finish()
    }
    el.addEventListener("animationend", onEnd)
    // Rede de segurança: se o animationend não disparar (animação ausente,
    // nó escondido), resolve assim mesmo após o tempo máximo esperado.
    setTimeout(finish, fallbackMs)
    el.classList.add(exitClass)
  })
}
